import { create } from "zustand";
import { persist } from "zustand/middleware";

export type TransactionViewMode = "default" | "custom";

interface TransactionViewState {
  /**
   * A participant's personal choice of transaction set, keyed by
   * `${userId}_${cohortId}`. A missing entry means "follow whatever the
   * trainer set on the cohort".
   *
   * The trainer's `transactionViewMode` switches the whole cohort at once, so
   * this override is what lets one person go back to the primary transactions
   * (or move ahead to the custom ones) without changing anyone else's view.
   * Progress for the two sets is stored in separate fields on the user's
   * progress document, so switching never discards work.
   */
  overrides: Record<string, TransactionViewMode>;
  setViewOverride: (scopeKey: string, mode: TransactionViewMode | null) => void;
}

export const viewScopeKey = (userId?: string, cohortId?: string) =>
  userId && cohortId ? `${userId}_${cohortId}` : "";

export const useTransactionViewStore = create<TransactionViewState>()(
  persist(
    (set) => ({
      overrides: {},

      setViewOverride: (scopeKey, mode) =>
        set((state) => {
          if (!scopeKey) return state;
          const overrides = { ...state.overrides };
          if (mode == null) {
            delete overrides[scopeKey];
          } else {
            overrides[scopeKey] = mode;
          }
          return { overrides };
        }),
    }),
    { name: "ame-transaction-view-overrides" }
  )
);
